// ChartVariantSales.js
import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import axiosInstance from '../utils/axiosInstance';
import { plotlyStyle, formatLabel } from '../utils/plotlyStyle';
import { Stack, Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { MESSAGES } from '../constants/messages';

const ChartProductVariantSales = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const now = dayjs();

    const defaultFrom = now.subtract(11, 'month').startOf('month');
    const defaultTo = now.endOf('month');

    const [from, setFrom] = useState(defaultFrom);
    const [to, setTo] = useState(defaultTo);

    const fetchData = async (params = {}) => {
        setLoading(true);

        try {
            const response = await axiosInstance.get('/chart/variant-sales', { params });
            setData(response.data || []);
            setError(null);
        } catch (err) {
            setError('データの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const setThisMonth = () => {
        const f = dayjs().startOf('month');
        const t = dayjs().endOf('month');

        setFrom(f);
        setTo(t);

        fetchData({
            from: f.format('YYYY-MM-DD'),
            to: t.format('YYYY-MM-DD'),
        });
    };

    const setLast3Months = () => {
        const f = dayjs().subtract(2, 'month').startOf('month');
        const t = dayjs().endOf('month');

        setFrom(f);
        setTo(t);

        fetchData({
            from: f.format('YYYY-MM-DD'),
            to: t.format('YYYY-MM-DD'),
        });
    };

    const setLast12Months = () => {
        const f = dayjs().subtract(11, 'month').startOf('month');
        const t = dayjs().endOf('month');

        setFrom(f);
        setTo(t);

        fetchData({
            from: f.format('YYYY-MM-DD'),
            to: t.format('YYYY-MM-DD'),
        });
    };

    if (loading) return <Typography>{MESSAGES.LOADING}</Typography>;
    if (error) return <Typography>{error || MESSAGES.LOAD_FAILED}</Typography>;

    const labels = data.map(item => formatLabel(item.sku));

    const salesData = [
        {
            x: labels,
            y: data.map(item => Number(item.total_sales || 0)),
            text: data.map(item => item.sku),
            type: 'bar',
            name: '売上高',
            yaxis: 'y1',
            offsetgroup: 'sales',
            marker: { color: plotlyStyle.colors.bar },
            hovertemplate: '%{text}<br>売上高: %{y:,.0f}円<extra></extra>',
        },
        {
            x: labels,
            y: data.map(item => Number(item.total_quantity || 0)),
            text: data.map(item => item.sku),
            type: 'bar',
            name: '注文数',
            yaxis: 'y2',
            offsetgroup: 'qty',
            marker: { color: plotlyStyle.colors.secondary },
            hovertemplate: '%{text}<br>注文数: %{y:,.0f}<extra></extra>',
        },
    ];

    const layout = {
        ...plotlyStyle.layout,
        barmode: 'group',
        title: {
            text:
                from && to
                    ? `品番別売上高と注文数（${from.format('YYYY-MM')}〜${to.format('YYYY-MM')}）`
                    : '直近12ヶ月の品番別売上高と注文数',
        },
        xaxis: {
            ...plotlyStyle.layout.xaxis,
        },
        yaxis: {
            ...plotlyStyle.layout.yaxis,
            title: { text: '売上高' },
            tickformat: ',d',
            separatethousands: true,
            exponentformat: 'none',
            showexponent: 'none',
            automargin: true,
        },
        yaxis2: {
            ...plotlyStyle.layout.yaxis,
            title: { text: '注文数' },
            overlaying: 'y',
            side: 'right',
            tickformat: ',d',
            separatethousands: true,
            exponentformat: 'none',
            showexponent: 'none',
            automargin: true,
        },

        legend: {
            orientation: 'h',
            y: 1,
            x: 0.7,
        },
    };

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <DatePicker
                            label="開始月"
                            views={['year', 'month']}
                            value={from ?? defaultFrom}
                            onChange={setFrom}
                            maxDate={to}
                            format="YYYY年M月"
                            slotProps={{ textField: { size: 'small' } }}
                        />

                        <Typography>〜</Typography>

                        <DatePicker
                            label="終了月"
                            views={['year', 'month']}
                            value={to ?? defaultTo}
                            onChange={setTo}
                            minDate={from}
                            format="YYYY年M月"
                            slotProps={{ textField: { size: 'small' } }}
                        />

                        <Button
                            size="small"
                            variant="contained"
                            disabled={!from || !to}
                            onClick={() =>
                                fetchData({
                                    from: from.startOf('month').format('YYYY-MM-DD'),
                                    to: to.endOf('month').format('YYYY-MM-DD'),
                                })
                            }
                        >
                            適用
                        </Button>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={setThisMonth}>
                            今月
                        </Button>

                        <Button size="small" variant="outlined" onClick={setLast3Months}>
                            直近3ヶ月
                        </Button>

                        <Button size="small" variant="outlined" onClick={setLast12Months}>
                            直近12ヶ月
                        </Button>
                    </Stack>
                </Stack>
            </LocalizationProvider>

            <div>
                <Plot
                    data={salesData}
                    layout={layout}
                    config={{
                        responsive: true,
                        displayModeBar: false,
                    }}
                    style={{
                        width: '100%',
                        height: '100%',
                        minHeight: 500,
                    }}
                    useResizeHandler={true}
                />
            </div>
        </>
    );
};

export default ChartProductVariantSales;